import { ActionTriggeredResult, FormSectionHandler } from '../entity.handler';
import { AssetCreateRequest, AssetLinkage, AssetLinkRequest, AssetResponse, AssetUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderDefinition, FormBuilderField } from 'app/shared/components/form-builder';
import { ComboField, DateTimeField, EntityUtils, TextField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { AdminWizardModalComponent } from '../../wizard/wizard-modal.component';
import { AssetDetailsSectionHandler } from './details.section';
import { AssetCategoriesSectionHandler } from './categories.section';
import { CustomFieldsSectionHandler } from '../shared/info.section';
import { AssetGroupingService } from 'app/services/assets/asset-grouping.service';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { AppService } from 'app/app.service';
import { MeasurementUnitsService } from 'app/services';
import * as _ from 'lodash';
import { cloneDeep } from 'lodash';
import { ERRORS } from 'app/shared/model/errors.model';


interface SectionValues {
    assetName: string;
    clientName: string;
    start: string;
    end: string;

}

export class AssetLinkingSectionHandler implements FormSectionHandler<AssetResponse, SectionValues> {

    type = 'form';
    icon = 'link';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    sourceAsset: AssetResponse;

    readonly = true;

    actions = [];

    warning = '';

    constructor(
        public app: AppService,
        public admin: AdminEditorService,
        public ownerId: string,
        private measurement: MeasurementUnitsService,
        private httpClient: HttpClient
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.LINKED_WITH.${key}`, values);
    }

    async applyUpdate(entity: AssetResponse, payload: any): Promise<AssetResponse> {
        try {
            if (payload.start) { // this is a link request
                const result = await this.admin.applyUpdate(
                    api => api.entities.linkAssets(entity.id, payload)
                )
                return result.source;
            }
            if (payload.unlink) { // this is an unlink request
                const result = await this.admin.applyUpdate(api => api.entities.unlinkAssets(entity.id, payload.unlink));
                return result.source;
            }

            if (payload.relink) { // this is an relink request
                let result = await this.admin.applyUpdate(api => api.entities.unlinkAssets(entity.id, payload.relink.assetId));
                result = await this.admin.applyUpdate(api => api.entities.linkAssets(entity.id, payload.relink));
                return result.source;
            }
        } catch (error) {
            this.showError(error);
        }

        return null;
    }

    async mapEntityToEditor(entity: AssetResponse): Promise<SectionValues> {
        this.sourceAsset = entity;
        const item = (entity.linkedTo && entity.linkedTo[0]) || entity.linkedFrom;
        if (item) {
            this.actions = [
                { id: 'unlink', name: this.translate('UNLINK'), showAlways: true },
            ];
            if (!entity.linkedFrom) {
                this.actions.push({ id: 'extend', name: this.translate('EXTEND'), showAlways: true });
            }
            this.description = entity.linkedFrom ? this.translate('DESC_LINKED_FROM') : this.translate('DESC_LINKED_TO');
            this.title = entity.linkedFrom ? this.translate('TITLE_FROM') : this.translate('TITLE_TO');
            if (item.end < moment.utc().toISOString()) {
                this.warning = this.translate('WARNING_EXPIRED');
            }
            return {
                assetName: item.asset.name,
                clientName: item.client.name,
                start: `${this.measurement.format(item.start, 'date').format} ${this.measurement.format(item.start, 'time').format}`,
                end: `${this.measurement.format(item.end, 'date').format} ${this.measurement.format(item.end, 'time').format}`,
            };
        }
        this.actions = [{ id: 'link', name: this.translate('LINK'), showAlways: true }];
        this.title = this.translate('TITLE');
        this.description = this.translate('DESC');
        this.warning = '';

        return null;
    }

    mapEditorToEntity(entity: AssetUpdateRequest, _values: SectionValues): Promise<AssetUpdateRequest> {
        return Promise.resolve(entity);
    }

    idNameToListItem(item: IdName): ListFieldValue<IdName> {
        return {
            id: item.id,
            name: item.name,
            icon: 'home',
            data: item,
        };
    }

    async getFields(entity: AssetResponse): Promise<FormBuilderField[]> {
        const item = (entity.linkedTo && entity.linkedTo[0]) || entity.linkedFrom;
        if (item) {
            return Promise.resolve([
                {
                    ...TextField('assetName', this.translate('FIELDS.LINKED_TO'), true, 0, 200), readonly: true,
                    getHref: (field: FormBuilderField, values: any) => {
                        const val = values[field.id];
                        return val ? `/admin/client/${item.client.id}/assets/grid/asset/${item.asset.id}` : null;
                    },
                },
                { ...TextField('clientName', this.translate('FIELDS.IN_CLIENT'), true, 0, 200), readonly: true },
                { ...TextField('start', this.translate('FIELDS.FROM'), true, 0, 200), readonly: true },
                { ...TextField('end', this.translate('FIELDS.TO'), true, 0, 200), readonly: true },
            ]).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
        }
        return [];
    }

    showAddAssetModal(destinationOwnerId: string, entity: any): Promise<AssetCreateRequest> {

        const grouping = AssetGroupingService.forClient(this.app, this.admin.i18n, destinationOwnerId);

        const handler = {
            getCreationSections: (ownerId: string): Promise<{ entityType: string, entity: AssetUpdateRequest, sections: FormSectionHandler<AssetResponse, any>[] }> => {
                return Promise.resolve({
                    entityType: 'asset',
                    entity: entity,
                    sections: [
                        new AssetDetailsSectionHandler(this.admin, ownerId, grouping, this.httpClient),
                        new AssetCategoriesSectionHandler(this.admin, ownerId, grouping),
                        new CustomFieldsSectionHandler<AssetResponse>(this.admin, ownerId, 'asset'),
                    ],
                });
            },
            createEntity: async (ownerId: string, item: AssetCreateRequest): Promise<AssetCreateRequest> => {
                // don't create anything, just pass the item back
                item.ownerId = ownerId;
                return item;
            },
        };

        return new Promise<any>((resolve, _reject) => {
            this.admin.modal.open(AdminWizardModalComponent, {
                data: {
                    ownerId: destinationOwnerId,
                    handler: handler,
                },
                actions: {
                    close: () => {
                        this.admin.modal.close();
                        resolve(null);
                    },
                    apply: (result) => {
                        this.admin.modal.close();
                        resolve(result);
                    },
                },
            });
        });

    }

    async actionTriggered(entity: AssetResponse, action: string): Promise<ActionTriggeredResult> {
        if (action === 'link') {
            const result = await this.showAddLinkModal();
            if (result) {
                return {
                    updatePayload: result,
                };

            }
        }
        if (action === 'unlink') {
            const item = (entity.linkedTo && entity.linkedTo[0]) || entity.linkedFrom;
            const result = await this.admin.modal.confirm(this.translate('UNLINK_CONFIRMATION'));
            if (result) {
                return {
                    updatePayload: {
                        unlink: item?.asset?.id,
                    },
                };
            }
        }
        if (action === 'extend') {
            const item = (entity.linkedTo && entity.linkedTo[0]);
            if (item) {
                const result = await this.showExtendLinkModal(item);
                if (result) {
                    return {
                        updatePayload: {
                            relink: result,
                        },
                    };
                }
            }



        }
    }

    showAddLinkModal(): Promise<AssetLinkRequest> {

        const getClientFields = (clientId: string): FormBuilderField[] => {
            return [
                ComboField('action', this.translate('FIELDS.ACTION'), true, [
                    { key: 'new', value: this.translate('FIELDS.NEW_ASSET') },
                    {
                        key: 'existing', value: this.translate('FIELDS.EXISTING_ASSET'), fields: [
                            { type: 'assetfilter', id: 'assetId', title: this.translate('FIELDS.ASSET'), required: true, max: 1, options: { ownerId: clientId } },
                        ],
                    },
                ]),
                
                { ...DateTimeField('start', this.translate(`FIELDS.FROM`), true), min: moment().toISOString() },
                DateTimeField('end', this.translate(`FIELDS.TO`), true),
            ];
        };

        let form: FormBuilderDefinition = {
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [{
                    type: 'clientfilter', id: 'client', title: this.translate('FIELDS.CLIENT'), required: true, max: 1,
                    // options: { ownerId: this.ownerId },
                    getText: (field, values) => values[field.id].name, readonly: false,
                    onChange: async (_f: FormBuilderField, vals: any) => {
                        // Make the video setting only appear after an asset is selected. We need this as we need the asset ID for the video settings. The code
                        // below adds the cameras, date, duration and offset fields and then updates the form value so that angular fires ngOnChanges on the form buider.
                        if (vals.client?.id) {
                            form.groups[0].fields = [
                                { ...form.groups[0].fields[0] },
                                ...getClientFields(vals.client.id),
                            ];
                            form = { ...form }; // make sure the reference gets updated to trigger change detection
                        }
                    },
                },
                ],
            }],
        };

        return this.admin.showFormModal(form, {
            client: null,
            assetId: null,
            asset: null,
            action: null,
            start: moment().toISOString(),
            end: null,
        }
        ).then(async result => {
            if (result) {
                if (result.action === 'new') {
                    const entity = {
                        name: this.sourceAsset.name,
                        color: this.sourceAsset.color,
                        assetType: this.sourceAsset.assetType,
                        fields: cloneDeep(this.sourceAsset.fields),
                    };
                    const createResult = await this.showAddAssetModal(result.client.id, entity);
                    if (createResult) {
                        return {
                            ...result,
                            asset: createResult,
                        };
                    }
                } else {
                    return result;
                }

            }
            return null;
        });


    }

    showExtendLinkModal(link: AssetLinkage): Promise<AssetLinkRequest> {

        const form: FormBuilderDefinition = {
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    DateTimeField('end', this.translate(`FIELDS.TO`), true),
                ],
            }],
        };

        return this.admin.showFormModal(form, {
            assetId: link.asset.id,
            start: link.start,
            end: link.end,
        }).then(async result => {
            return result;
        });

    }


    private showError(error: any) {
        if (error && error.name === ERRORS.VALIDATE_ERROR && error.data && error.data.fields) {
            const validationErrors = Object.keys(error.data.fields).reduce((result, field) => {
                result[field] = error.data.fields[field].message;
                return result;
            }, {});
            console.error('validationErrors', validationErrors);
            const message = Object.values(validationErrors).join(", ");
            this.admin.modal.error(message, error, 'ADMIN.MODALS.WIZARD.VALIDATION_ERRORS');
        } else {
            this.admin.modal.error("ERROR.UNEXPECTED_ERROR", error, 'ERROR.ERROR');
        }
    }

}
